import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/cn";

/**
 * StatusDot — small "is it working?" indicator. Combines colour AND a
 * shape pattern so colour-blind operators still get the signal:
 *
 *   ok       solid green dot
 *   info     blue diamond
 *   caution  triangle
 *   warning  square
 *   critical pulsing red dot with a ring
 *   offline  hollow grey ring
 *
 * Pulses respect prefers-reduced-motion via the global rule.
 */

export type StatusKind = "ok" | "info" | "caution" | "warning" | "critical" | "offline";

const COLOR: Record<StatusKind, string> = {
  ok:       "var(--success)",
  info:     "var(--accent)",
  caution:  "var(--caution)",
  warning:  "var(--warning)",
  critical: "var(--danger)",
  offline:  "var(--fg-disabled)",
};

/** 상태 라벨 i18n 키 — 렌더 시점에 t() 로 현지화한다. */
const LABEL_KEY: Record<StatusKind, string> = {
  ok: "common.status.ok",
  info: "common.status.info",
  caution: "common.status.caution",
  warning: "common.status.warning",
  critical: "common.status.critical",
  offline: "common.status.offline",
};

export interface StatusDotProps {
  kind: StatusKind;
  size?: number;
  pulse?: boolean;
  ariaLabel?: string;
  className?: string;
}

export function StatusDot({
  kind,
  size = 10,
  pulse = kind === "critical",
  ariaLabel,
  className,
}: StatusDotProps) {
  const { t } = useTranslation();
  const color = COLOR[kind];

  if (kind === "offline") {
    return (
      <span
        role="img"
        aria-label={ariaLabel ?? t(LABEL_KEY[kind])}
        className={cn("inline-block rounded-full border-2", className)}
        style={{ width: size, height: size, borderColor: color }}
      />
    );
  }

  if (kind === "caution") {
    // Triangle (CSS borders trick) — keeps it a single inline span.
    const half = size / 2;
    return (
      <span
        role="img"
        aria-label={ariaLabel ?? t(LABEL_KEY[kind])}
        className={cn("inline-block align-middle", className)}
        style={{
          width: 0,
          height: 0,
          borderLeft: `${half}px solid transparent`,
          borderRight: `${half}px solid transparent`,
          borderBottom: `${size}px solid ${color}`,
        }}
      />
    );
  }

  if (kind === "info") {
    // 마름모 — 초록 원(ok)과 한눈에 갈리는 모양이어야 색 없이도 구분된다.
    return (
      <span
        role="img"
        aria-label={ariaLabel ?? t(LABEL_KEY[kind])}
        className={cn("inline-block align-middle rotate-45 rounded-[2px]", className)}
        style={{ width: size * 0.86, height: size * 0.86, background: color }}
      />
    );
  }

  if (kind === "warning") {
    return (
      <span
        role="img"
        aria-label={ariaLabel ?? t(LABEL_KEY[kind])}
        className={cn("inline-block align-middle rounded-[2px]", className)}
        style={{ width: size, height: size, background: color }}
      />
    );
  }

  // ok / critical → filled circle, critical adds a pulsing ring.
  return (
    <span
      role="img"
      aria-label={ariaLabel ?? t(LABEL_KEY[kind])}
      className={cn("relative inline-block align-middle rounded-full", className)}
      style={{ width: size, height: size, background: color }}
    >
      {pulse && (
        <span
          aria-hidden
          className="absolute inset-0 rounded-full"
          style={{
            boxShadow: `0 0 0 0 ${color}`,
            animation: "pulse-ring 1.6s var(--ease-out-soft) infinite",
          }}
        />
      )}
    </span>
  );
}
